<template>
  <!-- The FAB's mini player (plan §4.7's sketch, card D2): the clock, the
       transport, the next event, and the door into the full page. It is its own
       component because it is the PLAYER SURFACE — mounted only while the pill
       is expanded, so its 1 Hz status poll lives exactly as long as the surface
       does (`useDemoClock({ poll: true })`). -->
  <div
    v-if="model"
    data-testid="demo-mini-player"
    class="mb-3 w-[19rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
    <div class="flex items-start justify-between gap-2 px-4 pt-3">
      <div class="min-w-0">
        <p class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
          <span class="h-1.5 w-1.5 flex-none rounded-full bg-amber-400"></span>
          {{ tt('Demo player', 'Demo predvajalnik') }}
        </p>
        <p class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{{ view.dayText.value }}</p>
      </div>
      <button
        type="button"
        data-testid="demo-mini-close"
        class="-mr-1 flex h-7 w-7 flex-none items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        :title="tt('Close the player', 'Zapri predvajalnik')"
        @click="emit('close')">
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>

    <div class="px-4 pt-1">
      <p class="text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">{{ model.clockLabel }}</p>
      <p class="text-xs text-gray-500 dark:text-gray-400">{{ model.dateLabel }}</p>
    </div>

    <div class="px-4 py-3">
      <DemoTransportControls
        :model="model"
        :pending="view.demo.playbackPending.value"
        @play="onPlay"
        @pause="onPause"
        @speed="onSpeed" />
    </div>

    <!-- The same narration board, one line (card D16): the mini player is where
         an operator keeps the run in view, so the caption belongs beside the
         clock rather than only on the 3D page. -->
    <div v-if="narration.visible.value" class="px-4 pb-2">
      <DemoNarration variant="card" />
    </div>

    <div class="border-t border-gray-100 px-4 py-2 dark:border-gray-700">
      <p v-if="model.nextEventLabel" class="truncate text-xs text-gray-600 dark:text-gray-300">
        <span class="text-gray-400 dark:text-gray-500">{{ tt('next:', 'naslednje:') }}</span>
        {{ model.nextEventLabel }}
      </p>
      <p v-else class="text-xs text-gray-400 dark:text-gray-500">
        {{ tt('No scheduled events yet', 'Ni predvidenih dogodkov') }}
      </p>
      <p v-if="error" class="mt-1 text-xs text-red-600 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <button
      type="button"
      data-testid="demo-open-player"
      class="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-blue-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-blue-400 dark:hover:bg-gray-700/50"
      @click="openPlayer">
      <span class="material-symbols-outlined text-base">open_in_full</span>
      {{ tt('Open player', 'Odpri predvajalnik') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import DemoNarration from './DemoNarration.vue';
import DemoTransportControls from './DemoTransportControls.vue';
import { useDemoNarration } from '../../composables/useDemoNarration';
import { useDemoPlayerView } from '../../composables/useDemoPlayerView';
import { getSettingsPath } from '../../composables/useIntegrationNavigation';
import { tt } from '../../utils/consoleText';

const emit = defineEmits<{ (event: 'close'): void }>();

const view = useDemoPlayerView({ poll: true });
/** The caption board's own reading (card D16) — the same one the 3D page reads. */
const narration = useDemoNarration();
const router = useRouter();
const route = useRoute();

const model = computed(() => view.model.value);
/** The last refusal, in the hub's own words (empty until one happens). */
const error = computed(() => view.demo.error.value);

function onPlay(): void {
  // This press is the user gesture the browser's autoplay policy wants: warm
  // the narration element here so the first cue of the day speaks (card D16).
  narration.unlock();
  void view.demo.playback('play');
}

function onPause(): void {
  void view.demo.playback('pause');
}

function onSpeed(rate: number): void {
  void view.demo.playback('speed', { rate });
}

function openPlayer(): void {
  emit('close');
  void router.push(getSettingsPath('/settings/demo', route));
}
</script>
